
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FeedbackRoundStatus } from '@brafton/feedback-api';
import { LoadingBar } from '@brafton/feedback-components';
import { PROJECT_STATUS_TILES, type ProjectStatusCounts } from './project-status';

/**
 * Projects overview banner — the full-width header that sits above every
 * Projects view (grid / list / kanban). Shows the project-collection title,
 * a "showing X of Y" summary, per-status counts, and the New Project action.
 *
 * Purely presentational: counts come in via inputs and the New Project action
 * is emitted back to the page, which owns the data and the drawer.
 */
@Component({
  selector: 'app-projects-overview-banner',
  imports: [MatButtonModule, MatIconModule, LoadingBar],
  templateUrl: './overview-banner.html',
  styleUrl: './overview-banner.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsOverviewBanner {
  /** Number of projects currently shown (after filters). */
  readonly filteredCount = input(0);

  /** Total number of projects across all statuses. */
  readonly total = input(0);

  /** Number of distinct organizations represented. */
  readonly organizationCount = input(0);

  /** Count of projects in each status, used to populate the status tiles. */
  readonly statusCounts = input<ProjectStatusCounts>({
    [FeedbackRoundStatus.Open]: 0,
    [FeedbackRoundStatus.Completed]: 0,
    [FeedbackRoundStatus.Closed]: 0,
    [FeedbackRoundStatus.Cancelled]: 0,
  });

  /** Hide the New Project button (e.g. while a project detail is open). */
  readonly showNewButton = input(true);

  /** Show the loading bar (while API requests are in flight). */
  readonly loading = input(false);

  /** Emitted when the user clicks New Project. */
  readonly newProject = output<void>();

  protected readonly tiles = PROJECT_STATUS_TILES;
}
